<script>
import {
    modalState, hideModal, showModal
} from "@app/store/modalStore.svelte.js";
import {
    authState
} from '@app/store/authStore.svelte.js';
import {
    environment
} from '@app/store/envStore.svelte.js';
import {
    playerState
} from '@app/store/playerStore.svelte.js';
import {
    eventGroupState, isSelectedEventGroupLocked, isEventGroupLockedExternally
} from "@app/store/eventGroupStore.svelte.js";
import {
    projectState
} from "@app/store/projectStore.svelte.js";
import {
    toast } from '@zerodevx/svelte-toast';
import {
    insertHistoryAction
} from "@app/store/historyStore.svelte.js";
import {
    fade
} from "svelte/transition";
import * as m from "@app/paraglide/messages.js";
import { hasOriginalText } from "@app/lib/eventGroupTypes.js";

import _EventGroup from "@cclib/classes/eventGroup.js";
import _Event from "@cclib/classes/event.js";
import {
    promptAdTemplateMinGap,
    buildAdEventGroupTemplate,
    fillAdTemplateFromGaps,
} from "@app/lib/adTemplate.js";

let eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};

let isLocked = $derived(isSelectedEventGroupLocked());
let isLockedExternally = $derived(isEventGroupLockedExternally());

function guardedShowModal(modalName) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    showModal(modalName);
}

function toggleEventGroupLock() {
    const group = eventGroupState[projectState.selected];
    if (!group) {
        return;
    }

    if (isEventGroupLockedExternally()) {
        toast.push(m.event_group_locked_by_integration(), { classes: ["toast-warning"] });
        return;
    }

    group.locked = !group.locked;

    insertHistoryAction({
        name: group.locked ? "lock event group" : "unlock event group",
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState),
        }],
    });

    toast.push(
        group.locked
            ? m.event_group_options_lock_success()
            : m.event_group_options_unlock_success(),
        { classes: ["toast-success"] }
    );

    if (group.locked) {
        hideModal();
    }

    return;
}

async function duplicateEventGroup() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    let duplicate = JSON.parse(JSON.stringify(eventGroupState[projectState.selected]));
    duplicate.name = duplicate.name + " (Copy)";
    // Use push method instead of array assignment
    eventGroupState.push({...(new _EventGroup(duplicate))});
    projectState.selected = eventGroupState.length - 1;

    insertHistoryAction({
        name: "duplicate event group", //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState),
        }],
    });

    toast.push(m.event_group_options_duplicate_success(), {
        classes: ["toast-success"]
    });

    hideModal();
}

async function deleteEventGroup() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    let eventGroupToDelete = projectState.selected;
    projectState.selected = false;

    // Instead of reassigning eventGroupState, remove items using array methods
    const deleteIndex = eventGroupToDelete;
    eventGroupState.splice(deleteIndex, 1);

    if (eventGroupState.length > 0) {
        projectState.selected = 0;
    }

    insertHistoryAction({
        name: "remove event group", //action name
        eventGroup: projectState.selected,
        snapshots: [
            {
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }, 
            {
                store: "projectState",
                value: JSON.stringify(projectState),
            }
        ],
    });

    toast.push(m.event_group_options_remove_success(), {classes: ['toast-success']});

    hideModal();
}

async function convertToSubtitles() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    let subtitleEventGroup = JSON.parse(JSON.stringify(eventGroupState[projectState.selected]));
    subtitleEventGroup.type = "subtitle";
    subtitleEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
    subtitleEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
    subtitleEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
    subtitleEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
    subtitleEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
    subtitleEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
    subtitleEventGroup.fontFamily = eventGroupDefaults.fontFamily || "plexMono";
    subtitleEventGroup.fontSize = eventGroupDefaults.fontSize || 16;
    subtitleEventGroup.events.forEach((event, index, events) => {
        events[index].alignment = "center";
    });
    // Use push method instead of array assignment
    eventGroupState.push({...(new _EventGroup(subtitleEventGroup))});
    projectState.selected = eventGroupState.length - 1;

    insertHistoryAction({
        name: "convert to subtitles", //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState),
        }],
    });

    toast.push(m.event_group_options_convert_success(), {classes: ['toast-success']});

    hideModal();
}

async function createAdTemplate() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const minGap = await promptAdTemplateMinGap(m);
    if (minGap === null) {
        return;
    }

    try {
        toast.push(m.event_group_options_create_ad_template_pending(), {classes: ['toast-info']});

        const adDefaults = JSON.parse(localStorage.getItem("cc-ad-defaults-new")) || {};
        const adEventGroupTemplate = buildAdEventGroupTemplate({
            name: m.ad_template_name({ index: eventGroupState.length }),
            eventGroupDefaults,
            adDefaults,
        });

        fillAdTemplateFromGaps(adEventGroupTemplate, {
            sourceEvents: eventGroupState[projectState.selected].events,
            projectDuration: playerState.duration,
            minGapSeconds: minGap,
        });

        eventGroupState.push(adEventGroupTemplate);
        projectState.selected = eventGroupState.length - 1;

        insertHistoryAction({
            name: "create AD Template",
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }],
        });

        toast.push(m.event_group_options_create_ad_template_success(), {classes: ['toast-success']});
        hideModal();
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.event_group_options_create_ad_template_failed({ error: err.message }), {classes: ['toast-danger']});
    }
}

function mergeText(){
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        //Copy the selected Event Group and merge the original and text properties together.
        let mergedEventGroup = JSON.parse(JSON.stringify(eventGroupState[projectState.selected]));
        mergedEventGroup.id = crypto.randomUUID();
        mergedEventGroup.type = "subtitle";
        mergedEventGroup.name = mergedEventGroup.name + " (Merged)";
        mergedEventGroup.linkedGroup = false;
        mergedEventGroup.events.forEach((event, index, events) => {
            events[index].text = event.original + " " + event.text;
            events[index].original = ""; 
        });

        // Use push method instead of array assignment
        eventGroupState.push(mergedEventGroup);
        projectState.selected = eventGroupState.length - 1;    
    } catch (err){
        console.log(err);
    } finally {
        hideModal();
    }
}

function mergeEventsBySpeakers() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        // Create a copy of the selected event group
        let mergedEventGroup = JSON.parse(JSON.stringify(eventGroupState[projectState.selected]));
        mergedEventGroup.id = crypto.randomUUID();
        mergedEventGroup.name = mergedEventGroup.name + " (Merged Speakers)";
        
        // Create a new array to hold the merged events
        let mergedEvents = [];
        
        // Skip processing if there are no events
        if (!mergedEventGroup.events || mergedEventGroup.events.length === 0) {
            throw new Error(m.event_group_options_no_events_to_merge());
        }
        
        // Function to check if two speaker arrays match
        const speakersMatch = (speakers1, speakers2) => {
            // Handle null/undefined/empty arrays
            if (!speakers1) speakers1 = [];
            if (!speakers2) speakers2 = [];
            
            // Must have same length
            if (speakers1.length !== speakers2.length) return false;
            
            // Sort and normalize both arrays for comparison
            // For each speaker, extract ID (primary) or name (fallback) for comparison
            const normalized1 = [...speakers1].map(s => {
                if (typeof s === 'object' && s !== null) {
                    return s.id || (s.name || '').trim().toLowerCase();
                }
                return String(s).trim().toLowerCase();
            }).sort();
            
            const normalized2 = [...speakers2].map(s => {
                if (typeof s === 'object' && s !== null) {
                    return s.id || (s.name || '').trim().toLowerCase();
                }
                return String(s).trim().toLowerCase();
            }).sort();
            
            // Compare each element
            return normalized1.every((s, i) => s === normalized2[i]);
        };
        
        // Start with the first event
        let currentMergedEvent = {...mergedEventGroup.events[0]};
        
        // Process subsequent events
        for (let i = 1; i < mergedEventGroup.events.length; i++) {
            const currentEvent = mergedEventGroup.events[i];
            
            // If speakers match, merge the events
            if (speakersMatch(currentMergedEvent.speakers, currentEvent.speakers)) {
                // Update end time to the later of the two events
                currentMergedEvent.end = currentEvent.end;
                
                // Combine text with a line break
                currentMergedEvent.text += "\n" + currentEvent.text;
                
                // Merge metadata (keeping them unique)
                if (currentEvent.recipients?.length) {
                    currentMergedEvent.recipients = [...(currentMergedEvent.recipients || []), 
                        ...(currentEvent.recipients || [])];
                    // Remove duplicates - handle both object and primitive types
                    currentMergedEvent.recipients = removeDuplicateObjects(currentMergedEvent.recipients, 'id');
                }
                
                if (currentEvent.tags?.length) {
                    currentMergedEvent.tags = [...(currentMergedEvent.tags || []), 
                        ...(currentEvent.tags || [])];
                    // Remove duplicates - for tags which are typically strings
                    currentMergedEvent.tags = [...new Set(currentMergedEvent.tags)];
                }
            } else {
                // Speakers don't match, so add the current merged event to our results
                // and start a new merged event
                mergedEvents.push(currentMergedEvent);
                currentMergedEvent = {...currentEvent};
            }
        }
        
        // Add the last merged event
        mergedEvents.push(currentMergedEvent);
        
        // Update the event group with merged events
        mergedEventGroup.events = mergedEvents;
        
        // Add the new event group to the project
        eventGroupState.push(mergedEventGroup);
        projectState.selected = eventGroupState.length - 1;
        
        insertHistoryAction({
            name: "merge events by speakers",
            eventGroup: projectState.selected,
            snapshots: [{
                store: "eventGroupState",
                value: JSON.stringify(eventGroupState),
            }],
        });
        
        toast.push(m.event_group_options_merge_by_speakers_success(), {classes: ['toast-success']});
    } catch (err) {
        console.error("Error merging events by speakers:", err);
        toast.push(m.event_group_options_merge_by_speakers_failed({ error: err.message }), {classes: ['toast-danger']});
    } finally {
        hideModal();
    }
}

// Helper function to remove duplicate objects from array based on a property
function removeDuplicateObjects(array, prop) {
    if (!array || !array.length) return [];
    
    const seen = new Map();
    return array.filter(item => {
        // Handle primitive values
        if (typeof item !== 'object' || item === null) {
            const itemStr = String(item).trim().toLowerCase();
            if (!seen.has(itemStr)) {
                seen.set(itemStr, true);
                return true;
            }
            return false;
        }
        
        // Handle objects with the specified property
        const key = item[prop] || JSON.stringify(item);
        if (!seen.has(key)) {
            seen.set(key, true);
            return true;
        }
        return false;
    });
}

// Track active tab - removing 'convert' from possible tabs
let activeTab = $state('general');

function setActiveTab(tab) {
    activeTab = tab;
}
</script>

<div transition:fade={{ duration: 100 }} 
    class="modal {modalState.selected === 'eventGroupOptions' ? 'show d-block' : ''}" 
    role="dialog" 
    aria-labelledby="optionsTitle"
    aria-modal="true"
    tabindex="-1" 
    id="EventGroupOptionsModal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
            <div class="modal-header border-0 pb-0">
                <h5 class="modal-title" id="optionsTitle">{m.event_group_options_title()}</h5>
                <button type="button" 
                    class="btn-close" 
                    id="optionsClose"
                    aria-label={m.event_group_options_close_label()} 
                    onclick={hideModal}></button>
            </div>
            <div class="modal-body rounded-bottom">
                <!-- Tab navigation -->
                <ul class="nav nav-tabs mb-3" role="tablist">
                    <li class="nav-item" role="presentation">
                        <button class="nav-link {activeTab === 'general' ? 'active' : ''}" 
                            onclick={() => setActiveTab('general')} 
                            id="general-tab" 
                            type="button" 
                            role="tab" 
                            aria-controls="general-tab-pane" 
                            aria-selected={activeTab === 'general'}>
                            <i class="bi bi-gear" aria-hidden="true"></i> {m.event_group_options_tab_general()}
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link {activeTab === 'merge' ? 'active' : ''}" 
                            onclick={() => setActiveTab('merge')} 
                            id="merge-tab" 
                            type="button" 
                            role="tab" 
                            aria-controls="merge-tab-pane" 
                            aria-selected={activeTab === 'merge'}>
                            <i class="bi bi-union" aria-hidden="true"></i> {m.event_group_options_tab_merge()}
                        </button>
                    </li>
                    <li class="nav-item" role="presentation">
                        <button class="nav-link {activeTab === 'track' ? 'active' : ''}" 
                            onclick={() => setActiveTab('track')} 
                            id="track-tab" 
                            type="button" 
                            role="tab" 
                            aria-controls="track-tab-pane" 
                            aria-selected={activeTab === 'track'}>
                            <i class="bi bi-layers" aria-hidden="true"></i> {m.event_group_options_tab_track()}
                        </button>
                    </li>
                </ul>
                
                <!-- Tab content with card-style buttons -->
                <div class="tab-content">
                    <!-- General tab -->
                    <div class="tab-pane fade {activeTab === 'general' ? 'show active' : ''}" 
                        id="general-tab-pane" 
                        role="tabpanel" 
                        aria-labelledby="general-tab" 
                        tabindex="0">
                        <div class="row row-cols-1 row-cols-md-2 g-3">
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => duplicateEventGroup()}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-files fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_duplicate()}</h5>
                                    </div>
                                </button>
                            </div>
                            
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("eventGroupMetadata")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-tags fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_edit_metadata()}</h5>
                                    </div>
                                </button>
                            </div>                           
                            
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("segmentEventGroup")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-scissors fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_segment_events()}</h5>
                                    </div>
                                </button>
                            </div>

                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("editEventGroup")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-sliders fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_settings()}</h5>
                                    </div>
                                </button>
                            </div>
                        </div>

                        <!-- Lock / Unlock -->
                        <div class="row mt-4">
                            <div class="col-12">
                                <button type="button" class="card h-100 action-card btn p-0 w-100 {isLocked ? 'bg-warning warning-card' : ''}" disabled={isLockedExternally} title={isLockedExternally ? m.event_group_locked_by_integration() : undefined} onclick={() => toggleEventGroupLock()}>
                                    <div class="card-body text-center py-4 {isLocked ? 'text-dark' : ''}">
                                        <i class="bi bi-{isLocked ? 'unlock' : 'lock'}-fill fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{isLocked ? m.event_group_options_unlock() : m.event_group_options_lock()}</h5>
                                    </div>
                                </button>
                            </div>
                        </div>
                        
                        <!-- Delete button as a full row -->
                        <div class="row mt-4">
                            <div class="col-12">
                                <button type="button" class="card action-card bg-danger danger-card btn p-0 w-100" disabled={isLocked} onclick={() => deleteEventGroup()}>
                                    <div class="card-body text-center py-4 text-white">
                                        <i class="bi bi-trash fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_delete()}</h5>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Merge & Combine tab -->
                    <div class="tab-pane fade {activeTab === 'merge' ? 'show active' : ''}" 
                        id="merge-tab-pane" 
                        role="tabpanel" 
                        aria-labelledby="merge-tab" 
                        tabindex="0">
                        <div class="row row-cols-1 row-cols-md-2 g-3">
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("mergeEventGroup")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-arrows-collapse fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_merge_groups()}</h5>
                                    </div>
                                </button>
                            </div>
                            
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => mergeEventsBySpeakers()}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-people fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_merge_by_speakers()}</h5>
                                    </div>
                                </button>
                            </div>
                            
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("translationMerge")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-translate fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_translation_merge()}</h5>
                                    </div>
                                </button>
                            </div>

                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("metadataMerge")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-database-add fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_metadata_merge()}</h5>
                                    </div>
                                </button>
                            </div>
                            
                            {#if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "transcription"}
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => convertToSubtitles()}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-chat-text fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_convert_to_subtitles()}</h5>
                                    </div>
                                </button>
                            </div>
                            {/if}
                            
                            {#if eventGroupState[projectState.selected] && hasOriginalText(eventGroupState[projectState.selected].type)}
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => mergeText()}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-intersect fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_merge_text()}</h5>
                                    </div>
                                </button>
                            </div>
                            {/if}
                            
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" onclick={() => showModal("compareEventGroups")}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-arrow-left-right fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_compare_event_groups()}</h5>
                                    </div>
                                </button>
                            </div>
                            
                            {#if authState.ad || !environment.online}
                            <div class="col">
                                <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => createAdTemplate()}>
                                    <div class="card-body text-center py-4">
                                        <i class="bi bi-mic fs-3 mb-2" aria-hidden="true"></i>
                                        <h5 class="card-title">{m.event_group_options_create_ad_template()}</h5>
                                    </div>
                                </button>
                            </div>
                            {/if}
                        </div>
                    </div>
                    
                    <!-- Track Operations tab -->
                    <div class="tab-pane fade {activeTab === 'track' ? 'show active' : ''}" 
                        id="track-tab-pane" 
                        role="tabpanel" 
                        aria-labelledby="track-tab" 
                        tabindex="0">
                        <div class="row row-cols-1 row-cols-md-2 g-3">

                            {#if eventGroupState[projectState.selected]?.type === 'multiview'}
                                <div class="col">
                                    <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("splitMultiviewGroup")}>
                                        <div class="card-body text-center py-4">
                                            <i class="bi bi-layout-split fs-3 mb-2" aria-hidden="true"></i>
                                            <h5 class="card-title">{m.event_group_options_split_multiview()}</h5>
                                        </div>
                                    </button>
                                </div>
                            {/if}

                            {#if eventGroupState.length > 1}
                                <div class="col">
                                    <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("copyEventText")}>
                                        <div class="card-body text-center py-4">
                                            <i class="bi bi-clipboard fs-3 mb-2" aria-hidden="true"></i>
                                            <h5 class="card-title">{m.event_group_options_copy_event_text()}</h5>
                                        </div>
                                    </button>
                                </div>
                                <div class="col">
                                    <button type="button" class="card h-100 action-card btn p-0 w-100" disabled={isLocked} onclick={() => guardedShowModal("eventGroupSync")}>
                                        <div class="card-body text-center py-4">
                                            <i class="bi bi-arrow-repeat fs-3 mb-2" aria-hidden="true"></i>
                                            <h5 class="card-title">{m.event_group_options_event_group_sync()}</h5>
                                        </div>
                                    </button>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style>
    .modal-body {
        min-height: 45vh;
        background: linear-gradient(135deg, var(--bs-body-bg) 0%, var(--bs-light-bg-subtle) 100%);
    }
    
    .modal-content {
        border: none;
        border-radius: 1rem;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        background-color: var(--bs-body-bg);
    }
    
    .modal-header {
        background: linear-gradient(135deg, var(--bs-body-bg) 0%, var(--bs-light-bg-subtle) 100%);
        border-radius: 1rem 1rem 0 0;
        padding: 1.5rem 2rem 1rem;
    }
    
    .modal-title {
        font-weight: 600;
        color: var(--bs-body-color);
        font-size: 1.5rem;
    }
    
    /* Tab styling */
    .nav-tabs {
        border: none;
        background: var(--bs-light-bg-subtle);
        border-radius: 0.75rem;
        padding: 0.25rem;
        margin-bottom: 2rem;
    }
    
    .nav-tabs .nav-link {
        border: none;
        border-radius: 0.5rem;
        padding: 0.75rem 1.25rem;
        color: var(--bs-secondary-color);
        font-weight: 500;
        transition: all 0.3s ease;
        margin: 0.125rem;
        background: transparent;
    }
    
    .nav-tabs .nav-link.active {
        background: var(--bs-primary);
        color: white;
        transform: translateY(-1px);
    }
    
    .nav-tabs .nav-link:hover:not(.active) {
        background: rgba(var(--bs-primary-rgb), 0.1);
        color: var(--bs-primary);
        transform: translateY(-1px);
    }
    
    /* Modern card styling */
    .action-card {
        transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        border: none;
        background: linear-gradient(135deg, var(--bs-body-bg) 0%, var(--bs-light-bg-subtle) 100%);
        border-radius: 1rem;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
        display: block;
        text-align: center;
        font-weight: normal;
        position: relative;
        overflow: hidden;
    }

    .action-card::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--bs-primary);
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 0;
    }
    
    .action-card .card-body {
        position: relative;
        z-index: 1;
        padding: 2rem 1rem;
    }
    
    .action-card i {
        font-size: 2.5rem;
        color: var(--bs-primary);
        margin-bottom: 1rem;
        transition: all 0.3s ease;
        display: block;
    }
    
    .action-card .card-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--bs-body-color);
        margin: 0;
        transition: all 0.3s ease;
    }
    
    .action-card:hover {
        transform: translateY(-1px) scale(1.01);
    }
    
    .action-card:hover::before {
        opacity: 1;
    }
    
    .action-card:hover i,
    .action-card:hover .card-title {
        color: white;
        transform: scale(1.1);
    }
    
    .action-card:focus {
        outline: none;
        transform: translateY(-4px);
    }
    
    .action-card:active {
        transform: translateY(-1px) scale(0.98);
    }
    
    /* Danger card specific styling */
    .danger-card {
        background: linear-gradient(135deg, var(--bs-danger) 0%, #c82333 100%);
        color: white;
    }
    
    .danger-card::before {
        background: linear-gradient(135deg, #bd2130 0%, #a71e2a 100%);
    }
    
    .danger-card i,
    .danger-card .card-title {
        color: white;
    }
    
    .danger-card:focus {
        box-shadow: 0 0 0 3px rgba(var(--bs-danger-rgb), 0.4);
    }

    /* Warning card specific styling (Unlock when locked) */
    .warning-card {
        background: linear-gradient(135deg, var(--bs-warning) 0%, #e0a800 100%);
        color: #212529;
    }

    .warning-card::before {
        background: linear-gradient(135deg, #e0a800 0%, #c69500 100%);
    }

    .warning-card i,
    .warning-card .card-title {
        color: #212529;
    }

    .warning-card:focus {
        box-shadow: 0 0 0 3px rgba(var(--bs-warning-rgb), 0.4);
    }
    
    /* Button reset styles */
    button.card {
        appearance: none;
        background-color: transparent;
        border: none;
    }
    
    /* Row spacing improvements */
    .row {
        margin-bottom: 1rem;
    }
    
    .row:last-child {
        margin-bottom: 0;
    }
    
    /* Tab content styling */
    .tab-content {
        padding: 0.5rem;
    }
    
    /* Subtle animations for tab switching */
    .tab-pane {
        opacity: 0;
        transition: opacity 0.3s ease;
    }
    
    .tab-pane.active {
        opacity: 1;
    }
    
    /* Responsive improvements */
    @media (max-width: 768px) {
        .modal-header {
            padding: 1rem 1.5rem 0.75rem;
        }
        
        .modal-title {
            font-size: 1.3rem;
        }
        
        .action-card .card-body {
            padding: 1.5rem 0.75rem;
        }
        
        .action-card i {
            font-size: 2rem;
            margin-bottom: 0.75rem;
        }
        
        .action-card .card-title {
            font-size: 1rem;
        }
        
        .nav-tabs .nav-link {
            padding: 0.5rem 0.75rem;
            font-size: 0.9rem;
        }
    }
</style>
